import {useRef, useContext, useMemo} from 'react'
import LocaleContext from 'antd/es/locale/context'
import {DatePicker as AntDatePicker} from 'antd'
import momentGenerateConfig from '@rc-component/picker/generate/moment'
import moment from 'moment'
import utils from '@eitje/utils'
import _ from 'lodash'

export const MomentDatePicker = AntDatePicker.generatePicker(momentGenerateConfig)

export const BaseDatePicker = props => {
	const contextLocale = useContext(LocaleContext)
	const activeLocale = contextLocale?.default || contextLocale

	return <MomentDatePicker {...props} locale={activeLocale?.DatePicker} />
}

const defaultFormat = ['DD-MM-YYYY', 'YYYY-MM-DD']

const disabledAfterToday = date => date && date.endOf('day') <= moment().endOf('day')
const disabledBeforeToday = date => date && date.endOf('day') >= moment().endOf('day')

const disabledTodayAndBefore = date => date && date.endOf('day') > moment().endOf('day')
const disabledTodayAndAfter = date => date && date.endOf('day') < moment().endOf('day')

export const isDateDisabled = (
	date,
	{
		disabledAfter,
		disabledBefore,
		disabledDates,
		disabledRanges,
		disabledFn,
		formData,
		field,
		isEnd = field === 'end_date',
		isStart = field === 'start_date',
		startDateField = 'start_date',
		endDateField = 'end_date',
		futureDisabled,
		pastDisabled,
		pastDisabledToday,
		futureDisabledToday,
	},
) => {
	let valid = true

	if (disabledFn) return disabledFn(date)

	const _disabledAfter = utils.funcOrVal(disabledAfter, formData)
	const _disabledBefore = utils.funcOrVal(disabledBefore, formData)

	if (futureDisabled) valid = disabledAfterToday(date)

	if (futureDisabledToday && valid) valid = disabledTodayAndAfter(date)

	if (isStart && valid && formData[endDateField]) {
		valid = date <= moment(formData[endDateField], defaultFormat).startOf('day')
	}

	if (isEnd && valid && formData[startDateField]) {
		valid = date >= moment(formData[startDateField], defaultFormat).startOf('day')
	}

	if (pastDisabled && valid) valid = disabledBeforeToday(date)

	if (pastDisabledToday && valid) valid = disabledTodayAndBefore(date)

	if (_disabledAfter && valid) {
		valid = date < moment(_disabledAfter, defaultFormat).startOf('day')
	}

	if (_disabledBefore && valid) {
		valid = date > moment(_disabledBefore, defaultFormat).endOf('day')
	}

	if (disabledRanges && valid && date) {
		valid = !disabledRanges.some(r => r.contains(date))
	}

	if (disabledDates && valid && date) {
		valid = !disabledDates.some(disabledDate => date.isSame(moment(disabledDate, defaultFormat), 'day'))
	}

	return !valid
}

export const LegacyDatePicker = props => {
	const picker = useRef(null)
	const {
		innerClass,
		classNames: incomingClassNames = {
			popup: {root: ''},
		},
		customFormat,
		onChange,
		value,
		readOnly,
		renderExtraFooter,
		formData,
		defaultPickerValue,
		...rest
	} = props

	const format = customFormat || defaultFormat
	const val = value ? moment(value, format) : null

	const condProps = {}
	if (readOnly) {
		condProps['open'] = false
		condProps['allowClear'] = false
	}

	const _defPickerValue = utils.funcOrVal(value || defaultPickerValue, formData)
	const defPickerValue = _defPickerValue && moment(_defPickerValue, format)

	const hasExtraFooter = _.isFunction(renderExtraFooter)
	const footerRenderer = hasExtraFooter
		? () => renderExtraFooter({value: val, onChange, startValue: formData?.['start_date'], picker})
		: undefined

	const className = utils.makeCns(innerClass, 'eitje-date-picker')

	const classNames = useMemo(() => {
		const popup = incomingClassNames.popup || {}
		const root = [hasExtraFooter ? 'has-extra-footer' : '', popup.root || ''].filter(Boolean).join(' ')

		return {
			...incomingClassNames,
			popup: {
				...popup,
				root,
			},
		}
	}, [incomingClassNames, hasExtraFooter])

	const pickerProps = readOnly ? {open: false, allowClear: false} : {}

	return (
		<BaseDatePicker
			ref={picker}
			{...pickerProps}
			className={className}
			classNames={classNames}
			disabledDate={date => isDateDisabled(date, props)}
			format={format}
			{...rest}
			value={val}
			onChange={(date, dateString) => onChange(dateString)}
			defaultPickerValue={defPickerValue ?? undefined}
			renderExtraFooter={footerRenderer}
			previewValue={false}
		/>
	)
}
